import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
// icons
import { Plus, ChevronDown } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import { IActor } from '@newo/api/gateway';
import AddPersonaForm from '@/modules/Sandbox/components/AddPersonaForm';
import AddActorForm from '@/modules/Sandbox/components/AddActorForm';
import SelectPersonaForm from '@/modules/Sandbox/components/SelectPersonaForm';
import SelectActorForm from '@/modules/Sandbox/components/SelectActorForm';
// utils
import useSandboxStore from '@/modules/Sandbox/Sandbox.store.ts';
import { IChatPersona } from '@/modules/Chat/Chat.types.ts';
// assets
import styles from '@/modules/Sandbox/components/UserControl/UserControl.module.css';

const UserControl = () => {
    const { userPersona, setUserPersona, userActor, setUserActor } =
        useSandboxStore(
            useShallow((state) => ({
                userPersona: state.userPersona,
                setUserPersona: state.setUserPersona,
                userActor: state.userActor,
                setUserActor: state.setUserActor
            }))
        );

    const userPersonaName = userPersona ? userPersona.name : 'Select persona';

    const handlePersonaSelect = (persona?: IChatPersona, actor?: IActor) => {
        setUserPersona(persona);
        setUserActor(actor);
    };

    const handleActorSelect = (actor?: IActor) => {
        setUserActor(actor);
    };

    return (
        <div className={styles.wrapper}>
            <div className="flex flex-nowrap gap-2 mb-2">
                <div className="shrink-0 w-12 opacity-50 leading-none">
                    User:
                </div>
                <PopoverButton
                    className={styles.button}
                    trigger={
                        <>
                            <div className={styles.name}>{userPersonaName}</div>
                            <ChevronDown className={styles.arrow} />
                        </>
                    }
                >
                    {(close) => (
                        <SelectPersonaForm
                            persona={userPersona}
                            onClose={close}
                            onSubmit={handlePersonaSelect}
                        />
                    )}
                </PopoverButton>
                <PopoverButton trigger={<Plus />}>
                    {(close) => (
                        <AddPersonaForm
                            currentActor={userActor}
                            onClose={close}
                            onSubmit={handlePersonaSelect}
                        />
                    )}
                </PopoverButton>
            </div>
            <div
                className={clsx(
                    'flex flex-nowrap gap-2',
                    !userPersona && 'pointer-events-none opacity-40'
                )}
            >
                <div className="w-12 opacity-50 leading-none">Actor:</div>
                <PopoverButton
                    className={styles.button}
                    trigger={
                        <>
                            {userActor ? (
                                <div className={styles.meta}>
                                    <div
                                        className={styles.name}
                                    >{`${userActor.integration_idn} / ${userActor.connector_idn}`}</div>
                                    <div>{userActor.external_id}</div>
                                </div>
                            ) : (
                                <div className={styles.name}>Select actor</div>
                            )}
                            <ChevronDown className={styles.arrow} />
                        </>
                    }
                >
                    {(close) => (
                        <SelectActorForm
                            actor={userActor}
                            personaId={userPersona?.id ?? ''}
                            onClose={close}
                            onSubmit={handleActorSelect}
                        />
                    )}
                </PopoverButton>
                <PopoverButton trigger={<Plus />}>
                    {(close) =>
                        !!userPersona && (
                            <AddActorForm
                                currentActor={userActor}
                                persona={userPersona}
                                onClose={close}
                                onSubmit={handleActorSelect}
                            />
                        )
                    }
                </PopoverButton>
            </div>
        </div>
    );
};

export default UserControl;
